import React, { useEffect, useRef } from "react";
import { VictoryChart } from "victory-chart";
import { VictoryTooltip } from "victory-tooltip";
import { VictoryScatter } from "victory-scatter";
import html2canvas from "html2canvas";
import { VictoryVoronoiContainer } from "victory-voronoi-container";
import { VictoryAxis } from "victory-axis";
import { VictoryArea } from "victory-area";
import { TiCardPrevalenceData } from "../../../../@types";
import { postfixNumber } from "../../utils";
import TooltipIcon from "../../../../../../components/TooltipIcon";
import RenderWhen from "../../../../../../components/RenderWhen";
import Typography from "../../../../../../components/typography/Typography";

interface Props {
  prevelanceData: Array<TiCardPrevalenceData>;
  prerenderGraphImage?: boolean;
}

const TiCardPrevalenceGraph = ({
  prevelanceData,
  prerenderGraphImage
}: Props) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartImgRef = useRef<HTMLDivElement>(null);

  const chartData = prevelanceData.map(x => ({
    x: x.from,
    y: x.samples_count,
    l: `${x.from} - ${x.to}: ${postfixNumber(x.samples_count)}`
  }));

  const chartMax = Math.max(...prevelanceData.map(x => x.samples_count));
  const chartHasData = chartData.length !== 0 && chartMax !== 0;

  useEffect(() => {
    if (chartRef.current && prerenderGraphImage) {
      html2canvas(chartRef.current).then(canvas => {
        const img = document.createElement("img");
        img.src = canvas.toDataURL();
        chartImgRef.current?.appendChild(img);
      });
    }
  }, [prerenderGraphImage]);

  return (
    <div className="ticard__content__chart">
      <div className={"ticard__content__chart__title"}>
        Malware Family Prevalence
        <TooltipIcon
          icon={"info-circle"}
          message={
            "The occurrence of samples belonging to the observed Malware Family."
          }
          placement={"top"}
          style={{ display: "inline", marginLeft: "3px" }}
        />
      </div>
      <div className={"k-pdf-hide-in-pdf"} ref={chartRef}>
        <RenderWhen
          condition={chartHasData}
          fallback={
            <Typography
              variant={"body1"}
              className={"ticard__content__chart__no-data"}
              data-testid="ticard_prevalence_no_data"
            >
              No data available for the last 6 months
            </Typography>
          }
        >
          <div data-testid="ticard_prevalence_chart">
            <VictoryChart
              domain={{ y: [0, chartMax] }}
              height={80}
              domainPadding={{ y: [0, 2] }}
              padding={{ top: 10, bottom: 5, left: 40, right: 10 }}
              scale={{ x: "linear", y: "linear" }}
              containerComponent={<VictoryVoronoiContainer />}
            >
              <VictoryAxis
                style={{
                  axis: { strokeWidth: 0 },
                  tickLabels: {
                    display: "block",
                    fontFamily: "Roboto",
                    fontSize: 10
                  }
                }}
                crossAxis={false}
                tickFormat={t => window.tcbase.formatNumber(t, true)}
                dependentAxis
              />
              <VictoryScatter
                data={chartData}
                y={d => d.y}
                style={{
                  data: { fill: chartHasData ? "#d00000" : "transparent" }
                }}
                size={chartHasData ? 2 : 0}
                labels={({ datum: d }) => d.l}
                labelComponent={
                  chartHasData ? (
                    <VictoryTooltip
                      horizontal
                      orientation="top"
                      dy={2}
                      cornerRadius={0}
                      pointerLength={0}
                      flyoutPadding={{ top: 5, bottom: 5, left: 5, right: 5 }}
                      flyoutStyle={{
                        stroke: "transparent",
                        fill: "#d00000"
                      }}
                      style={{
                        fill: "#ffffff",
                        fontSize: 11,
                        fontFamily: "Roboto"
                      }}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              <VictoryArea
                data={chartData}
                y={d => d.y}
                style={{
                  data: {
                    strokeWidth: 1,
                    stroke: "#d00000",
                    fill: "#FEAFAF"
                  }
                }}
              />
            </VictoryChart>
          </div>

          <div className={"ticard__content__chart__labels"}>
            <span>6 months ago</span>
            <span>3 months ago</span>
            <span>Today</span>
          </div>
        </RenderWhen>
      </div>
      <div
        className={"k-pdf-show-in-pdf"}
        ref={chartImgRef}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default TiCardPrevalenceGraph;
